import React, { useState } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useResponsiveDesign } from '../../../../responsiveDesign/responsiveDesign';
import { useLocale } from '../../../../contexts/LocaleContext';
import { appLogger } from '../../../../../domain/services/analytics/appLogger';
import { ExpauseButton, ButtonVariant, ButtonIconPosition } from '../../controls/ExpauseButton';
import { qaAttr } from '../../../../utils/qaAttr';
import type { StepNav } from '../stepNavContext';

const CHEVRON_LEFT = '/assets/vectors/chevron_left.svg';
const CHEVRON_RIGHT = '/assets/vectors/chevron_right.svg';

export interface StepNavFooterProps {
  nav: StepNav;
  onPreviousDefault: () => void;
}

export const StepNavFooter: React.FC<StepNavFooterProps> = ({
  nav,
  onPreviousDefault,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showPrevious = nav.showPrevious ?? (nav.onPrevious != null);
  const nextVisible = !nav.hideNext && nav.onNext != null;

  if (!showPrevious && !nextVisible) return null;

  const handlePrevious = () => {
    if (isSubmitting) return;
    (nav.onPrevious ?? onPreviousDefault)();
  };

  const handleNext = async () => {
    if (!nav.onNext || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await nav.onNext();
    } catch (error) {
      appLogger.error('step onNext failed', 'StepNavFooter.handleNext', { error: error as Error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextIcon = nav.nextIcon === null ? undefined : nav.nextIcon ?? CHEVRON_RIGHT;
  const previousIcon = nav.previousIcon === null ? undefined : nav.previousIcon ?? CHEVRON_LEFT;

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        backgroundColor: theme.modalBackground,
        display: 'flex',
        flexDirection: 'row',
        gap: responsive.spacing.xl,
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {showPrevious && (
        <ExpauseButton
          {...qaAttr({ id: 'stepper-back' })}
          variant={ButtonVariant.secondary}
          icon={previousIcon}
          iconPosition={ButtonIconPosition.left}
          onClick={handlePrevious}
          disabled={isSubmitting}
        >
          {nav.previousLabel ?? l10n.common.previous}
        </ExpauseButton>
      )}

      {nextVisible && (
        <ExpauseButton
          {...qaAttr({ id: 'stepper-next' })}
          variant={ButtonVariant.secondary}
          icon={nextIcon}
          iconPosition={ButtonIconPosition.right}
          onClick={handleNext}
          disabled={nav.canGoNext === false || isSubmitting}
          loading={isSubmitting}
        >
          {nav.nextLabel ?? l10n.common.next}
        </ExpauseButton>
      )}
    </div>
  );
};
